import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import {
  Box, Paper, Typography, Button, FormControl, InputLabel, Select, MenuItem, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TablePagination, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, IconButton, Alert, CircularProgress,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  VisibilityOutlined as ViewIcon,
  Close as CloseIcon,
  Check as ApproveIcon,
  Block as CancelIcon,
} from "@mui/icons-material";
import {
  NAVY, cardSx, headCellSx, EmployeeSelect, RequestStatusChip, fmtDate,
} from "../../../components/leave/LeaveCommon";
import { fetchLeaveRequests, approveLeave, rejectLeave, cancelLeave } from "../../../actions/Leave/LeaveRequestAction";
import { extractRows } from "../../../service/Leave/leaveHelpers";

// Cancelling an approved leave is switched off for now; set to true to bring the button back
const ALLOW_CANCEL_APPROVED = false;

const ROWS = 8;
const FILTERS = [
  { value: "ALL", label: "All Requests" },
  { value: "Pending", label: "Pending" },
  { value: "Approved", label: "Approved" },
  { value: "Rejected", label: "Rejected" },
  { value: "Cancelled", label: "Cancelled" },
];

const Stat = ({ label, value, color, bg }) => (
  <Paper elevation={0} sx={{ ...cardSx, px: 2, py: 1.5, backgroundColor: bg, borderColor: `${color}33` }}>
    <Typography sx={{ fontSize: 22, fontWeight: 700, color }}>{value}</Typography>
    <Typography sx={{ fontSize: 10.5, fontWeight: 600, color, letterSpacing: 0.6, textTransform: "uppercase" }}>{label}</Typography>
  </Paper>
);

const Detail = ({ label, children }) => (
  <Box>
    <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", letterSpacing: 1, textTransform: "uppercase" }}>{label}</Typography>
    <Box sx={{ fontSize: 14, color: "#1e293b", mt: 0.25 }}>{children || "—"}</Box>
  </Box>
);

// The attachment is either a base64 data-URL or a path/URL on the server
const openAttachment = (value) => {
  try {
    if (value.startsWith("data:")) {
      const [header, b64] = value.split(",");
      const mime = (header.match(/data:(.*?);base64/) || [])[1] || "application/octet-stream";
      const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: mime }));
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      return;
    }
    const base = (axios.defaults.baseURL || "").replace(/\/$/, "");
    const url = /^https?:\/\//i.test(value) ? value : `${base}/${value.replace(/^\//, "")}`;
    window.open(url, "_blank", "noopener,noreferrer");
  } catch (e) {
    console.error("Failed to open attachment", e);
  }
};

const LeaveApprovalTab = ({ notify }) => {
  const dispatch = useDispatch();

  // ── Redux data (loaded by the LeaveApplication page) ───────
  const { data: rawEmployees } = useSelector((s) => s.getAllEmployees) || {};
  const { data: leaveTypes = [] } = useSelector((s) => s.leaveTypes) || {};
  const { data: requests = [], loading, error: loadError } = useSelector((s) => s.leaveRequests) || {};
  const userId = useSelector((s) => s.auth?.serviceNo);

  const [employee, setEmployee] = useState(null); // null = all employees
  const [filter, setFilter] = useState("ALL");
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const employees = useMemo(
    () =>
      extractRows(rawEmployees)
        .map((e) => {
          const serviceNo = String(e.serviceNo ?? e.ServiceNo ?? "").trim();
          return {
            serviceNo,
            name: e.reportName || e.ReportName || e.name || serviceNo,
            department: e.department || e.DepartmentCode || e.Designation || "",
          };
        })
        .filter((e) => e.serviceNo),
    [rawEmployees]
  );
  const empName = useMemo(() => Object.fromEntries(employees.map((e) => [e.serviceNo, e.name])), [employees]);
  const typeName = useMemo(() => Object.fromEntries(leaveTypes.map((t) => [t.id, t.name])), [leaveTypes]);

  const filtered = useMemo(
    () =>
      requests
        .filter((r) => (filter === "ALL" || r.status === filter) && (!employee || r.serviceNo === employee.serviceNo))
        .sort((a, b) => b.from.localeCompare(a.from)),
    [requests, filter, employee]
  );
  const rows = filtered.slice(page * ROWS, page * ROWS + ROWS);

  const stats = useMemo(
    () => ({
      total: requests.length,
      pending: requests.filter((r) => r.status === "Pending").length,
      approved: requests.filter((r) => r.status === "Approved").length,
      rejected: requests.filter((r) => r.status === "Rejected").length,
    }),
    [requests]
  );

  const open = (r) => { setSelected(r); setRemarks(""); setError(""); setConfirmCancel(false); };
  const close = () => { if (saving) return; setSelected(null); setRemarks(""); setError(""); setConfirmCancel(false); };

  // always show the latest version of the request that is open
  const current = selected ? requests.find((r) => r.key === selected.key) || selected : null;

  const decide = async (kind) => {
    if (!current) return;
    if (current.status !== "Pending") return setError("This request has already been processed.");
    if (!userId) return setError("Your user session was not found. Please sign in again.");
    if (!current.formId) return setError("This request has no form id (HLF_FORM_ID), so it can't be processed.");
    if (kind === "REJECT" && !remarks.trim()) return setError("Please enter a remark when rejecting a request.");

    setSaving(true);
    setError("");
    const run = kind === "APPROVE" ? approveLeave : rejectLeave;
    const res = await dispatch(run({ formId: current.formId, formNo: current.formNo, userId, reason: remarks.trim() }));
    setSaving(false);
    if (!res.ok) return setError(res.message);

    notify?.(
      `Leave request #${current.formNo} ${kind === "APPROVE" ? "approved" : "rejected"}`,
      kind === "APPROVE" ? "success" : "info"
    );
    setSelected(null);
    setRemarks("");
  };

  const cancel = async () => {
    if (!current || current.status !== "Approved") return;
    if (!userId) return setError("Your user session was not found. Please sign in again.");
    if (!current.formId) return setError("This request has no form id (HLF_FORM_ID), so it can't be cancelled.");

    setSaving(true);
    setError("");
    const res = await dispatch(cancelLeave({ formId: current.formId, formNo: current.formNo, userId }));
    setSaving(false);
    if (!res.ok) {
      setConfirmCancel(false);
      return setError(res.message);
    }
    notify?.(`Leave request #${current.formNo} cancelled`, "info");
    setSelected(null);
    setConfirmCancel(false);
  };

  return (
    <Box>
      {/* Filters */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 2, alignItems: "center" }}>
        <EmployeeSelect
          label="Employee" placeholder="All Employees" options={employees}
          value={employee} onChange={(e) => { setEmployee(e); setPage(0); }}
          sx={{ width: 300 }}
        />
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filter} label="Status" onChange={(e) => { setFilter(e.target.value); setPage(0); }}>
            {FILTERS.map((f) => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
          </Select>
        </FormControl>
        <Button
          onClick={() => {
            // Refresh also clears the filters (all employees, all requests, first page)
            setEmployee(null);
            setFilter("ALL");
            setPage(0);
            dispatch(fetchLeaveRequests());
          }}
          disabled={loading}
          startIcon={<RefreshIcon sx={loading ? { animation: "spin 1s linear infinite", "@keyframes spin": { to: { transform: "rotate(360deg)" } } } : {}} />}
          sx={{ textTransform: "none", fontWeight: 600, color: "#334155", border: "1px solid #e2e8f0", height: 40, px: 2 }}
        >
          Refresh
        </Button>
      </Box>

      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}

      {/* Stats */}
      <Box sx={{ display: "grid", gap: 1.5, mb: 2, gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" } }}>
        <Stat label="Total" value={stats.total} color="#334155" bg="#f1f5f9" />
        <Stat label="Pending" value={stats.pending} color="#b45309" bg="#fffbeb" />
        <Stat label="Approved" value={stats.approved} color="#047857" bg="#ecfdf5" />
        <Stat label="Rejected" value={stats.rejected} color="#b91c1c" bg="#fef2f2" />
      </Box>

      {/* Table */}
      <Paper elevation={0} sx={{ ...cardSx, overflow: "hidden" }}>
        <TableContainer>
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                {["Form #", "Employee", "Leave Type", "From", "To", "Days", "Status", "Actions"].map((h) => (
                  <TableCell key={h} sx={headCellSx} align={h === "Status" || h === "Actions" ? "center" : "left"}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && requests.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5 }}><CircularProgress size={24} /></TableCell>
                </TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5, color: "#94a3b8" }}>
                    {employee
                      ? `No ${filter !== "ALL" ? filter.toLowerCase() + " " : ""}leave requests found for this employee.`
                      : filter === "ALL" ? "No leave requests found." : `No ${filter.toLowerCase()} requests found.`}
                  </TableCell>
                </TableRow>
              )}
              {rows.map((r) => {
                const pending = r.status === "Pending";
                return (
                  <TableRow key={r.key} hover>
                    <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>{r.formNo}</TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{empName[r.serviceNo] || r.serviceNo}</TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{typeName[r.leaveTypeId] || r.leaveTypeId}</TableCell>
                    <TableCell sx={{ fontSize: 13, whiteSpace: "nowrap" }}>{fmtDate(r.from)}</TableCell>
                    <TableCell sx={{ fontSize: 13, whiteSpace: "nowrap" }}>{fmtDate(r.to)}</TableCell>
                    <TableCell sx={{ fontSize: 13, fontWeight: 700 }}>{r.days}</TableCell>
                    <TableCell align="center"><RequestStatusChip status={r.status} /></TableCell>
                    <TableCell align="center">
                      <Button
                        size="small" onClick={() => open(r)} startIcon={<ViewIcon sx={{ fontSize: 16 }} />}
                        sx={{
                          textTransform: "none", fontWeight: 600, fontSize: 12,
                          color: pending ? NAVY : "#64748b", backgroundColor: pending ? "#e8eefc" : "#f1f5f9",
                          "&:hover": { backgroundColor: pending ? "#d6e0fa" : "#e2e8f0" },
                        }}
                      >
                        {pending ? "Review" : "View"}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div" count={filtered.length} page={page} onPageChange={(e, p) => setPage(p)}
          rowsPerPage={ROWS} rowsPerPageOptions={[ROWS]}
        />
      </Paper>

      {/* View Dialog */}
      <Dialog open={!!current} onClose={close} maxWidth="sm" fullWidth>
        {current && (
          <>
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, color: NAVY, fontWeight: 700 }}>
              <Box sx={{ flex: 1 }}>
                Leave Request #{current.formNo}
                {empName[current.serviceNo] ? ` - ${empName[current.serviceNo]}` : ""}
              </Box>
              <RequestStatusChip status={current.status} />
              <IconButton size="small" onClick={close}><CloseIcon fontSize="small" /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: "repeat(2, 1fr)" }}>
                <Detail label="Form No">{current.formNo}</Detail>
                <Detail label="Service No">{current.serviceNo}</Detail>
                <Detail label="Leave Type">{typeName[current.leaveTypeId] || current.leaveTypeId}</Detail>
                <Detail label="Total Days"><b>{current.days} day(s)</b></Detail>
                <Detail label="From Date">{fmtDate(current.from)}</Detail>
                <Detail label="To Date">{fmtDate(current.to)}</Detail>
                <Detail label="Contact No">{current.contactNo}</Detail>
                <Detail label="Half Day">
                  {current.halfDay ? (current.halfDayType === "SECOND_HALF" ? "Second half" : "First half") : "No"}
                </Detail>
              </Box>

              <Box sx={{ mt: 2.5, pt: 2, borderTop: "1px solid #f1f5f9" }}>
                <Detail label="Reason">
                  <Box sx={{ p: 1.5, border: "1px solid #e2e8f0", borderRadius: "8px", backgroundColor: "#f8fafc", fontSize: 14 }}>
                    {current.reason || "No reason provided"}
                  </Box>
                </Detail>
              </Box>

              {current.attachmentPath && (
                <Box sx={{ mt: 2 }}>
                  <Detail label="Attachment">
                    <Button size="small" onClick={() => openAttachment(current.attachmentPath)} sx={{ textTransform: "none", p: 0, textDecoration: "underline" }}>
                      View Attachment
                    </Button>
                  </Detail>
                </Box>
              )}

              {current.status !== "Pending" && (current.approvedBy || current.rejectReason) && (
                <Box sx={{ mt: 2, display: "grid", gap: 2, gridTemplateColumns: "repeat(2, 1fr)" }}>
                  <Detail label="Decided By">{current.approvedBy}</Detail>
                  <Detail label="Remarks">{current.rejectReason}</Detail>
                </Box>
              )}

              {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

              {confirmCancel && (
                <Alert
                  severity="warning" sx={{ mt: 2 }}
                  action={
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <Button size="small" onClick={() => setConfirmCancel(false)} disabled={saving} sx={{ textTransform: "none", fontWeight: 600 }}>
                        No
                      </Button>
                      <Button size="small" color="error" variant="contained" onClick={cancel} disabled={saving} sx={{ textTransform: "none", fontWeight: 600 }}>
                        {saving ? "Cancelling..." : "Yes, cancel"}
                      </Button>
                    </Box>
                  }
                >
                  Cancel this approved leave? The employee's days will be given back.
                </Alert>
              )}

              {current.status === "Pending" && (
                <TextField
                  size="small" fullWidth label="Remarks" placeholder="Add a note (required when rejecting)..."
                  value={remarks} onChange={(e) => setRemarks(e.target.value)} sx={{ mt: 2.5 }}
                />
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={close} disabled={saving} sx={{ color: "#64748b", textTransform: "none", fontWeight: 600 }}>
                {current.status === "Pending" ? "Cancel" : "Close"}
              </Button>
              {ALLOW_CANCEL_APPROVED && current.status === "Approved" && !confirmCancel && (
                <Button
                  onClick={() => setConfirmCancel(true)} disabled={saving} startIcon={<CancelIcon />}
                  sx={{ color: "#dc2626", border: "1px solid #fecaca", textTransform: "none", fontWeight: 600 }}
                >
                  Cancel Leave
                </Button>
              )}
              {current.status === "Pending" && (
                <>
                  <Button
                    variant="contained" onClick={() => decide("REJECT")} disabled={saving}
                    startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <CloseIcon />}
                    sx={{ backgroundColor: "#dc2626", textTransform: "none", fontWeight: 600, "&:hover": { backgroundColor: "#b91c1c" } }}
                  >
                    Reject
                  </Button>
                  <Button
                    variant="contained" onClick={() => decide("APPROVE")} disabled={saving}
                    startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <ApproveIcon />}
                    sx={{ backgroundColor: NAVY, textTransform: "none", fontWeight: 600, "&:hover": { backgroundColor: "#0e3c66" } }}
                  >
                    Approve
                  </Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default LeaveApprovalTab;